<template>
  <validation-observer ref="navigationLinkObserver">
    <b-form @submit.prevent="handleSubmit" class="form-container">
      <b-row>
        <b-col md="6">
          <h4 class="mb-3">Konfiguracja belki nawigacyjnej</h4>

          <b-form-group class="mt-2" label="Link nadrzędny">
            <v-select
              id="v-parent-select"
              label="title"
              placeholder="Wybierz link nadrzędny"
              v-model="form.parent"
              :clearable="true"
              :deselectFromDropdown="false"
              :options="availableParents"
              :disabled="form.is_parent"
              @input="resetLanguage" />

            <small class="text-muted"> Wybierz link nadrzędny jeśli tworzysz tłumaczenie istniejącego linku </small>
          </b-form-group>

          <b-form-group class="mt-2" label="Język">
            <validation-provider #default="{ errors }" name="Język" rules="required">
              <v-select
                id="v-language-select"
                label="text"
                placeholder="Wybierz język"
                v-model="selectedLanguage"
                :clearable="false"
                :deselectFromDropdown="false"
                :options="availableLanguages"
                @input="onLanguageChange" />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group class="mt-2" label="Typ linku">
            <validation-provider #default="{ errors }" name="Typ linku" rules="required">
              <v-select
                id="v-link-type-select"
                label="text"
                placeholder="Wybierz typ linku"
                v-model="selectedLinkType"
                :clearable="false"
                :deselectFromDropdown="false"
                :options="linkTypes"
                :disabled="!selectedLanguage || !!form.parent"
                @input="onLinkTypeChange" />
              <small class="text-danger">{{ errors[0] }}</small>
              <small v-if="form.parent" class="text-muted d-block"> Typ linku dziedziczony z linku nadrzędnego </small>
            </validation-provider>
          </b-form-group>

          <b-form-group v-if="form.link_type === 'internal'" class="mt-2" label="Nazwa trasy (route name)">
            <validation-provider
              #default="{ errors }"
              name="Nazwa trasy"
              :rules="form.link_type === 'internal' ? 'required' : ''">
              <b-form-input
                id="v-route-name"
                placeholder="np. outlet, about-us, /bezwzgledna-sciezka"
                v-model="form.route_name"
                :disabled="!selectedLanguage" />
              <small class="text-danger">{{ errors[0] }}</small>
              <small class="text-muted d-block">
                <span v-if="form.parent && form.parent.route_name">
                  Dziedziczone z linku nadrzędnego: <strong>{{ form.parent.route_name }}</strong>
                </span>
                <span v-else>
                  <strong>Możesz używać:</strong><br />
                  • <strong>Nazwy tras według nazw własnych</strong> (np. <em>promotions</em> → /promocje w PL,
                  /sale w EN; <em>outlet</em> → /outlet)<br />
                  • <strong>Ścieżki bezwzględne</strong> rozpoczynające się od "/" (np. <em>/produkty</em>,
                  <em>/o-nas</em>)<br />
                </span>
              </small>
            </validation-provider>
          </b-form-group>

          <b-form-group v-if="form.link_type === 'external'" class="mt-2" label="URL zewnętrzny">
            <validation-provider
              #default="{ errors }"
              name="URL zewnętrzny"
              :rules="form.link_type === 'external' ? 'required|url' : ''">
              <b-form-input
                id="v-external-url"
                placeholder="https://example.com"
                v-model="form.external_url"
                :disabled="!selectedLanguage" />
              <small class="text-danger">{{ errors[0] }}</small>
              <small class="text-muted d-block">
                <span v-if="form.parent && form.parent.external_url">
                  Dziedziczone z linku nadrzędnego: <strong>{{ form.parent.external_url }}</strong>
                </span>
                <span v-else> Podaj pełny adres URL zewnętrznej strony </span>
              </small>
            </validation-provider>
          </b-form-group>

          <b-form-group class="mt-2" label="Zachowanie linku">
            <v-select
              id="v-target-select"
              label="text"
              placeholder="Wybierz metodę"
              v-model="selectedTarget"
              :clearable="false"
              :deselectFromDropdown="false"
              :options="targetOptions"
              :disabled="!selectedLanguage || !!form.parent"
              @input="onTargetChange" />
            <small v-if="form.parent" class="text-muted"> Zachowanie dziedziczone z linku nadrzędnego </small>
          </b-form-group>
        </b-col>

        <b-col md="6">
          <h4 class="mb-3">Dane linku</h4>

          <b-form-group class="mt-2" label="Tytuł">
            <validation-provider #default="{ errors }" name="Tytuł" rules="required|min:2|max:100">
              <b-form-input id="v-title" placeholder="Tytuł linku" v-model="form.title" :disabled="!selectedLanguage" />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>

          <b-form-group class="mt-2" label="Kolejność sortowania">
            <validation-provider #default="{ errors }" name="Kolejność sortowania" rules="required|numeric|min_value:0">
              <b-form-input
                id="v-sort-order"
                type="number"
                placeholder="0"
                v-model="form.sort_order"
                :disabled="!selectedLanguage" />
              <small class="text-danger">{{ errors[0] }}</small>
              <small class="text-muted d-block"> Niższa wartość = wyższa pozycja w menu </small>
            </validation-provider>
          </b-form-group>

          <b-form-group class="mt-2" label="Status">
            <b-form-checkbox v-model="form.is_active" :disabled="!selectedLanguage">
              Link aktywny (będzie wyświetlany w nawigacji)
            </b-form-checkbox>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row class="mt-4">
        <b-col>
          <div class="d-flex justify-content-end">
            <b-button
              variant="outline-secondary"
              class="mr-2"
              @click="$router.push({ name: 'navigation-management-index' })">
              Anuluj
            </b-button>
            <b-button type="submit" variant="primary" :disabled="!selectedLanguage">
              {{ isEditing ? 'Zaktualizuj' : 'Zapisz' }}
            </b-button>
          </div>
        </b-col>
      </b-row>
    </b-form>
  </validation-observer>
</template>

<script>
import { getLanguages } from '@/constants/languages';

export default {
  props: {
    navigationLinks: {
      type: Array,
      default: () => [],
    },
    navigationLink: {
      type: Object,
      default: null,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      form: {
        parent_id: null,
        lang: '',
        title: '',
        link_type: 'internal',
        route_name: '',
        external_url: '',
        target: '_self',
        is_active: true,
        sort_order: 0,
        parent: null,
        is_parent: false,
      },

      selectedLanguage: null,
      selectedLinkType: null,
      selectedTarget: null,

      availableLanguages: getLanguages().map((lang) => ({
        text: lang.value,
        value: lang.lang,
      })),

      linkTypes: [
        { text: 'Link wewnętrzny', value: 'internal' },
        { text: 'Link zewnętrzny', value: 'external' },
      ],

      targetOptions: [
        { text: 'To samo okno', value: '_self' },
        { text: 'Nowa karta', value: '_blank' },
      ],
    };
  },

  computed: {
    availableParents() {
      return this.navigationLinks.filter(
        (link) => link.parent_id === null && (!this.isEditing || link.id !== this.navigationLink?.id),
      );
    },
  },

  watch: {
    navigationLink: {
      handler(newVal) {
        if (newVal && this.isEditing) {
          this.populateForm();
        }
      },
      immediate: true,
    },

    'form.parent': {
      handler(newParent) {
        if (newParent && !this.isEditing) {
          this.inheritParentLinkData();
        }
      },
    },
  },

  mounted() {
    if (this.isEditing) {
      this.populateForm();
    }
  },

  methods: {
    populateForm() {
      if (!this.navigationLink) return;

      this.form = {
        parent_id: this.navigationLink.parent_id,
        lang: this.navigationLink.lang,
        title: this.navigationLink.title,
        link_type: this.navigationLink.link_type,
        route_name: this.navigationLink.route_name || '',
        external_url: this.navigationLink.external_url || '',
        target: this.navigationLink.target,
        is_active: this.navigationLink.is_active,
        sort_order: this.navigationLink.sort_order,
        parent: this.navigationLink.parent,
        is_parent: this.navigationLink.parent_id === null,
      };

      // Set selected values for v-select components
      this.selectedLanguage = this.availableLanguages.find((lang) => lang.value === this.navigationLink.lang);
      this.selectedLinkType = this.linkTypes.find((type) => type.value === this.navigationLink.link_type);
      this.selectedTarget = this.targetOptions.find((target) => target.value === this.navigationLink.target);
    },

    resetLanguage() {
      this.selectedLanguage = null;
      this.form.lang = '';
      this.inheritParentLinkData();
    },

    inheritParentLinkData() {
      if (this.form.parent) {
        // Inherit link type, route_name, external_url, and target from parent
        this.form.link_type = this.form.parent.link_type;
        this.form.route_name = this.form.parent.route_name || '';
        this.form.external_url = this.form.parent.external_url || '';
        this.form.target = this.form.parent.target || '_self';

        // Update the selected link type and target to reflect inherited values
        this.selectedLinkType = this.linkTypes.find((type) => type.value === this.form.link_type);
        this.selectedTarget = this.targetOptions.find((target) => target.value === this.form.target);
      }
    },

    onLanguageChange(language) {
      this.form.lang = language ? language.value : '';
    },

    onLinkTypeChange(linkType) {
      this.form.link_type = linkType ? linkType.value : 'internal';

      // Clear the other URL field when switching types
      if (this.form.link_type === 'internal') {
        this.form.external_url = '';
      } else {
        this.form.route_name = '';
      }
    },

    onTargetChange(target) {
      this.form.target = target ? target.value : '_self';
    },

    async handleSubmit() {
      const isValid = await this.$refs.navigationLinkObserver.validate();
      if (!isValid) return;

      // Prepare form data
      const formData = {
        parent_id: this.form.parent?.id || null,
        lang: this.form.lang,
        title: this.form.title,
        link_type: this.form.link_type,
        route_name: this.form.link_type === 'internal' ? this.form.route_name : null,
        external_url: this.form.link_type === 'external' ? this.form.external_url : null,
        target: this.form.target,
        is_active: this.form.is_active,
        sort_order: parseInt(this.form.sort_order),
      };

      this.$emit('onSubmit', formData);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@core/scss/vue/libs/vue-select.scss';

.form-container {
  max-width: 100%;
}

.border-top {
  border-top: 1px solid #dee2e6 !important;
}
</style>
